import { useState, useMemo, useEffect, useCallback } from "react";
import { useStudio } from "../studios/StudioContext";
import { buildClassTypeCss, typeKeywords } from "./classTypeStyles";
import {
  DAYS, MONTHS,
  toLocalDateStr, getDaysInMonth, getFirstDay, getWeekDates, workshopsForDate, spaceInfo,
} from "./calendarUtils";
import { PasswordModal, AdminEditPanel, AdminEventModal } from "./AdminComponents";
import { EventModal, WorkshopBookingModal, PassPurchaseModal } from "./BookingComponents";
import { MonthView, WeekView, DayView, ListView } from "./CalendarViews";
import { AgendaView } from "./AgendaView";
import { WorkshopBanner } from "./CalendarPills";
import { Elements } from "@stripe/react-stripe-js";
import { getStripe } from "./StripeCardForm";
import { useAdminApi } from "../pages/admin/adminUtils";
import "./YogaCalendar.css";



export default function YogaCalendar({ initialAdminMode = false, hideAdminBtn = false }) {
  const today = new Date();

  const [viewMode, setViewMode]       = useState(() => {
    if (typeof window === "undefined") return "month";
    if (initialAdminMode) {
      const saved = localStorage.getItem("florium_view_mode");
      if (saved === "list") return "list";
    }
    return window.innerWidth <= 480 ? "week" : "month";
  });
  const studio                                            = useStudio();
  const { API, AUTH, TOKEN }                              = useAdminApi();
  const openAdmin = () => { const url = `/${studio.studioId}/admin`; if (!window.open(url, "_blank")) window.location.assign(url); };
  const stripePromise                                     = getStripe(studio.stripePublishableKey);
  const [venues, setVenues]                               = useState([]);

  useEffect(() => {
    fetch(`${API}/venues`)
      .then(r => r.json())
      .then(d => { if (d.venues) setVenues(d.venues); })
      .catch(() => setVenues(studio?.venues ?? []));
  }, [API, studio?.venues]);
  const [currentDate, setCurrentDate]                     = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [focusDay, setFocusDay]                           = useState(today);
  const [classes, setClasses]                             = useState([]);
  const [taken, setTaken]                                 = useState({}); // "<classId>__<date>" → spots taken
  const [deepPrefill, setDeepPrefill]                     = useState(null); // { name, email } from a waitlist link
  const [cancelledDates, setCancelledDates]               = useState(new Set());
  const [dateOverrides, setDateOverrides]                 = useState(new Map());
  const [workshops, setWorkshops]                         = useState([]);
  const [privateBookings, setPrivateBookings]             = useState([]);
  const [scheduleLoading, setScheduleLoading]             = useState(true);
  const [adminMode]                                       = useState(initialAdminMode);
  const [passModalType, setPassModalType]                 = useState(null);
  const [announcement, setAnnouncement]                   = useState(null);
  const [classTypes, setClassTypes] = useState([]);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("announcementDismissed");
    fetch(`${API}/settings?studio=${studio.studioId}`)
      .then(r => r.json())
      .then(d => {
        const s = d.settings ?? d;
        setClassTypes(Array.isArray(s.classTypes) ? s.classTypes : []);
        if (!dismissed && s.announcementActive && s.announcementText) {
          setAnnouncement({ text: s.announcementText, url: s.announcementUrl && !/^([a-z]+:|\/)/i.test(s.announcementUrl) ? `https://${s.announcementUrl}` : s.announcementUrl, linkText: s.announcementLinkText });
        }
      })
      .catch(err => console.error("YogaCalendar: failed to load settings/announcement", err));
  }, [API, studio.studioId]);

  const changeViewMode = (mode) => {
    localStorage.setItem("florium_view_mode", mode);
    setViewMode(mode);
  };
  const [showPwModal, setShowPwModal]                     = useState(false);
  const [showEditPanel, setShowEditPanel]                 = useState(false);
  const [editingClassFromModal, setEditingClassFromModal] = useState(null);
  const [selectedEvent, setSelectedEvent]                 = useState(null);
  const [selectedDate, setSelectedDate]                   = useState(null);
  const [selectedWorkshop, setSelectedWorkshop]           = useState(null);
  const [isMobile, setIsMobile]                           = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 480
  );

  const year        = currentDate.getFullYear();
  const month       = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay    = getFirstDay(year, month);

  useEffect(() => {
    async function fetchSchedule() {
      try {
          const studioFilter = studio.studioId === "iy-sp" ? "&studio=iy-sp" : "";
          const [classRes, wsRes, cancelRes, overrideRes, privateBkRes] = await Promise.all([
          // Owner's token: the server only includes teacher pay (rates, ceiling, split) for the owner,
          // and the class / workshop edit forms need it. Public visitors get the schedule without it.
          fetch(`${API}/schedule/classes?includeEnded=true${studioFilter}`, TOKEN ? { headers: AUTH } : undefined),
          fetch(`${API}/schedule/workshops`, TOKEN ? { headers: AUTH } : undefined),
          fetch(`${API}/schedule/cancellations`),
          fetch(`${API}/schedule/overrides`),
          fetch(`${API}/bookings/private`),
        ]);
        const classData     = await classRes.json();
        const wsData        = await wsRes.json();
        const cancelData    = await cancelRes.json();
        const overrideData  = await overrideRes.json();
        const privateBkData = await privateBkRes.json();
        
        setClasses(classData.classes ?? []);
        setWorkshops(wsData.workshops ?? []);
        setPrivateBookings(privateBkData.bookings ?? []);
        try {
          const blkRes = await fetch(`${API}/blocks`);
          if (blkRes.ok) {
            const blkData = await blkRes.json();
            const blocks = (blkData.blocks ?? []).map(b => ({
              ...b,
              classId: `block__${b.date}__${(b.time || "").replace(/:/g, "")}`,
              _isBlock: true,
            }));
            setPrivateBookings(prev => [...prev, ...blocks]);
          }
        } catch { /* blocks route not on this tenant's server yet */ }
        setCancelledDates(new Set(
          (cancelData.cancellations ?? []).map(c => `${c.classId}__${c.date}`)
        ));
        setDateOverrides(new Map(
          (overrideData.overrides ?? []).map(o => [`${o.classId}__${o.date}`, { venue: o.venue }])
        ));
      } catch (e) {
        console.error("Failed to load schedule:", e);
      } finally {
        setScheduleLoading(false);
      }
    }
    fetchSchedule();
  }, [API, TOKEN]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    const handler = e => {
      setIsMobile(e.matches);
      setViewMode(v => {
        if (v === "list") return "list";
        if (e.matches && v === "month") return "week";
        if (!e.matches && v === "week") return "month";
        return v;
      });
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const openPass = params.get("openPass");
  if (openPass) setPassModalType(openPass);
  }, []);

  // Deep-link: ?openClass=<classId>&date=YYYY-MM-DD
  useEffect(() => {
    if (!classes.length) return;
    const params = new URLSearchParams(window.location.search);
    const openClassId = params.get("openClass");
    const openDate    = params.get("date");
    if (!openClassId || !openDate) return;
    const match = classes.find(c => c.id === openClassId);
    if (!match) return;
    const pEmail = params.get("email");
    if (pEmail) setDeepPrefill({ email: pEmail, name: params.get("name") || "" });
    setFocusDay(new Date(openDate + "T12:00:00"));
    setSelectedDate(new Date(openDate + "T12:00:00"));
    setSelectedEvent(match);
  }, [classes]);

  // Deep-link: ?openWorkshop=<workshopId>
  useEffect(() => {
    if (!workshops.length) return;
    const params = new URLSearchParams(window.location.search);
    const openWorkshopId = params.get("openWorkshop");
    if (!openWorkshopId) return;
    const match = workshops.find(w => w.id === openWorkshopId);
    if (match) setSelectedWorkshop(match);
  }, [workshops]);


  const prevMonth = () => setCurrentDate(new Date(year,month-1,1));
  const nextMonth = () => setCurrentDate(new Date(year,month+1,1));

  const weekDates = useMemo(() => {
    if (isMobile) {
      return [0,1].map(offset => {
        const d = new Date(focusDay);
        d.setDate(focusDay.getDate() + offset);
        return d;
      });
    }
    return getWeekDates(focusDay);
  }, [focusDay, isMobile]);

  const weekStep = isMobile ? 2 : 7;
  const prevWeek = () => { const d=new Date(focusDay); d.setDate(d.getDate()-weekStep); setFocusDay(d); };
  const nextWeek = () => { const d=new Date(focusDay); d.setDate(d.getDate()+weekStep); setFocusDay(d); };
  const prevDay  = () => { const d=new Date(focusDay); d.setDate(d.getDate()-1); setFocusDay(d); };
  const nextDay  = () => { const d=new Date(focusDay); d.setDate(d.getDate()+1); setFocusDay(d); };

  const calendarCells = useMemo(() => {
    const cells = [];
    for(let i=0;i<firstDay;i++) cells.push(null);
    for(let d=1;d<=daysInMonth;d++) cells.push(new Date(year,month,d));
    while(cells.length%7!==0) cells.push(null);
    return cells;
  }, [year,month,daysInMonth,firstDay]);

  const getVisibleClasses = (date) => {
    const dow = date.getDay(), dateStr = toLocalDateStr(date);
    const regular = classes
      .filter(c => c.dayOfWeek === dow && (!c.startDate || dateStr >= c.startDate) && (!c.endDate || dateStr <= c.endDate))
      .map(c => {
        const key = `${c._id}__${dateStr}`;
        const override = dateOverrides.get(key);
        return { ...c, ...(override || {}), _cancelled: cancelledDates.has(key), _overridden: !!override };
      });
    const privates = privateBookings
      .filter(b => b.date === dateStr)
      .map(b => {
        const timeRaw = b.classId.split("__")[2] ?? "";
        const time = timeRaw ? `${timeRaw.slice(0,2)}:${timeRaw.slice(2)}` : b.time ?? "";
        return { id: b._id, _id: b._id, classId: b.classId, title: b._isBlock ? (b.note || "Blocked") : "Private", time, duration: b._isBlock ? b.duration : 60, _isPrivate: true, _isBlock: !!b._isBlock, _cancelled: false };
      });
    return [...regular, ...privates].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  };

  const getPublicClasses = (date) => {
    const dow = date.getDay(), dateStr = toLocalDateStr(date);
    const regular = classes
      .filter(c => c.dayOfWeek === dow && (!c.startDate || dateStr >= c.startDate) && (!c.endDate || dateStr <= c.endDate))
      .map(c => {
        const key = `${c._id}__${dateStr}`;
        const override = dateOverrides.get(key);
        const merged = { ...c, ...(override || {}), _cancelled: cancelledDates.has(key) };
        return { ...merged, _space: spaceInfo(merged, taken[`${c.id}__${dateStr}`]) };
      });
    const privates = privateBookings
      .filter(b => b.date === dateStr)
      .map(b => {
        const timeRaw = b.classId.split("__")[2] ?? "";
        const time = timeRaw ? `${timeRaw.slice(0,2)}:${timeRaw.slice(2)}` : b.time ?? "";
        return { id: b._id, title: b._isBlock && b.note ? b.note : "Private", time, _isPrivate: true, _isBlock: !!b._isBlock, _cancelled: false };
      });
    return [...regular, ...privates].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  };

  // Spaces left on capped classes (today → 4 months ahead)
  const loadAvailability = useCallback(() => {
    const from = toLocalDateStr(new Date());
    const end  = new Date(); end.setDate(end.getDate() + 120);
    fetch(`${API}/bookings/availability?from=${from}&to=${toLocalDateStr(end)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.taken) setTaken(d.taken); })
      .catch(() => {});
  }, [API]);
  useEffect(() => {
    if (adminMode || !classes.some(c => c.cap)) return;
    loadAvailability();
  }, [adminMode, classes, loadAvailability]);

  const closeModal = () => {
    setSelectedEvent(null); setSelectedDate(null); setSelectedWorkshop(null); setDeepPrefill(null);
    if (!adminMode) loadAvailability();
  };

  const handleSaveClass = async (form) => {
    const isNew = !classes.find(c => c.id === form.id);
    if (isNew) {
      // One class per selected day (multi-day add / duplicate).
      const { _id, __v, createdAt, updatedAt, dayOfWeeks, _copyKey, ...base } = form;
      const days    = dayOfWeeks?.length ? dayOfWeeks : [form.dayOfWeek];
      const stamp   = Date.now();
      const created = [];
      for (const [i, d] of days.entries()) {
        const newClass = { ...base, dayOfWeek: d, id: `class_${stamp}${days.length > 1 ? `_${i}` : ""}`,
                           startDate: form.startDate || toLocalDateStr(new Date()) };
        const res = await fetch(`${API}/schedule/classes`, { method:"POST", headers:AUTH, body:JSON.stringify(newClass) });
        if (!res.ok) { alert(`Failed to create class: ${(await res.json().catch(()=>({}))).error || res.status}`); break; }
        created.push(newClass);
      }
      if (created.length) setClasses(prev => [...prev, ...created]);
    } else {
      const res = await fetch(`${API}/schedule/classes/${form.id}`, { method:"PATCH", headers:AUTH, body:JSON.stringify(form) });
      if (!res.ok) { alert(`Failed to save class: ${(await res.json().catch(()=>({}))).error || res.status}`); return; }
      setClasses(prev => prev.map(c => c.id === form.id ? {...form} : c));
    }
  };

  const handleDeleteClass = async (id) => {
    const res = await fetch(`${API}/schedule/classes/${id}`, { method:"DELETE", headers:AUTH });
    const data = await res.json();
    if (data.endDate) {
      setClasses(prev => prev.map(c => c.id === id ? { ...c, endDate: data.endDate } : c));
    }
  };

  const handleSaveWorkshop = async (form) => {
    const isNew = !workshops.find(w => w.id === form.id);
    if (isNew) {
      const newWS = { ...form, id: `ws_${Date.now()}` };
      const res = await fetch(`${API}/schedule/workshops`, { method:"POST", headers:AUTH, body:JSON.stringify(newWS) });
      if (!res.ok) { alert(`Failed to create workshop: ${(await res.json().catch(()=>({}))).error || res.status}`); return; }
      setWorkshops(prev => [...prev, newWS]);
    } else {
      const res = await fetch(`${API}/schedule/workshops/${form.id}`, { method:"PATCH", headers:AUTH, body:JSON.stringify(form) });
      if (!res.ok) { alert(`Failed to save workshop: ${(await res.json().catch(()=>({}))).error || res.status}`); return; }
      setWorkshops(prev => prev.map(w => w.id === form.id ? {...form} : w));
    }
  };

  const handleDeleteWorkshop = async (id) => {
    await fetch(`${API}/schedule/workshops/${id}`, { method:"DELETE", headers:AUTH });
    setWorkshops(prev => prev.filter(w => w.id !== id));
  };

  const handleCancelDate = async (classId, dateStr) => {
    await fetch(`${API}/schedule/cancellations`, { method:"POST", headers:AUTH, body:JSON.stringify({classId, date:dateStr}) });
    setCancelledDates(prev => { const n=new Set(prev); n.add(`${classId}__${dateStr}`); return n; });
    await fetch(`${API}/bookings/cancel-class`, { method:"PATCH", headers:AUTH, body:JSON.stringify({classId, date:dateStr}) });
    closeModal();
  };

  const handleRestoreDate = async (classId, dateStr) => {
    await fetch(`${API}/schedule/cancellations`, { method:"DELETE", headers:AUTH, body:JSON.stringify({classId, date:dateStr}) });
    setCancelledDates(prev => { const n=new Set(prev); n.delete(`${classId}__${dateStr}`); return n; });
    closeModal();
  };

  const handleSaveDateOverride = async (classId, dateStr, overrides) => {
    await fetch(`${API}/schedule/overrides`, { method:"POST", headers:AUTH, body:JSON.stringify({classId, date:dateStr, ...overrides}) });
    setDateOverrides(prev => { const next=new Map(prev); next.set(`${classId}__${dateStr}`, overrides); return next; });
  };

  const handleEditRecurring = (event) => {
    setShowEditPanel(true);
    setEditingClassFromModal(event);
  };

  const renderWorkshopBanners = (date) => {
    const dateStr = toLocalDateStr(date);
    return workshopsForDate(dateStr, workshops).map(w => (
      <WorkshopBanner key={w.id} workshop={w} dateStr={dateStr} onClick={setSelectedWorkshop} adminMode={adminMode} />
    ));
  };

  const sharedViewProps = {
    today, adminMode, getVisibleClasses, getPublicClasses,
    workshops, setSelectedEvent, setSelectedDate, setSelectedWorkshop,
  };

  return (
    <>
      <div className="cal-wrap">
        {announcement && !adminMode && (
          <div className="cal-announcement">
            <span>{announcement.text}{announcement.url && <a className="cal-announcement-link" href={announcement.url} target="_blank" rel="noopener noreferrer">{announcement.linkText || "Learn more"} →</a>}</span>
            <button className="cal-announcement-dismiss" onClick={() => {
              sessionStorage.setItem("announcementDismissed", "1");
              setAnnouncement(null);
            }}>✕</button>
          </div>
        )}
        <style>{buildClassTypeCss(classTypes)}</style>
        {scheduleLoading && <p className="cal-loading">Loading schedule…</p>}

        <div className={`cal-header${viewMode==="list"?" cal-header--list":""}`}>
          <h2 className="cal-title">
            {viewMode==="month"
              ? <><em>{MONTHS[month]}</em> {year}</>
              : viewMode==="week"
              ? isMobile
                ? <><em>{weekDates[0].toLocaleDateString("en-US",{weekday:"short"})}</em> {weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} &amp; <em>{weekDates[1].toLocaleDateString("en-US",{weekday:"short"})}</em> {weekDates[1].toLocaleDateString("en-US",{day:"numeric"})}</>
                : <><em>Week of</em> {weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})}</>
              : <><em>{DAYS[focusDay.getDay()]}</em> {focusDay.toLocaleDateString("en-US",{month:"long",day:"numeric"})}</>
            }
          </h2>
          <div className="cal-nav">
            <button className="nav-btn" onClick={viewMode==="month"?prevMonth:viewMode==="week"?prevWeek:prevDay}>‹</button>
            <span className="nav-label">
              {viewMode==="month"
                ? `${MONTHS[month].slice(0,3)} ${year}`
                : viewMode==="week"
                ? isMobile
                  ? `${weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${weekDates[1].toLocaleDateString("en-US",{month:"short",day:"numeric"})}`
                  : `${weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${weekDates[6].toLocaleDateString("en-US",{month:"short",day:"numeric"})}`
                : focusDay.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})
              }
            </span>
            <button className="nav-btn" onClick={viewMode==="month"?nextMonth:viewMode==="week"?nextWeek:nextDay}>›</button>
          </div>
          <div className="cal-header-right">
            <div className="view-toggle">
              <button data-view="month" className={`toggle-btn ${viewMode==="month"?"active":""}`} onClick={()=>changeViewMode("month")}>Month</button>
              <button data-view="week"  className={`toggle-btn ${viewMode==="week" ?"active":""}`} onClick={()=>changeViewMode("week")}>Week</button>
              <button data-view="day"   className={`toggle-btn ${viewMode==="day"  ?"active":""}`} onClick={()=>changeViewMode("day")}>Day</button>
              <button data-view="list"  className={`toggle-btn ${viewMode==="list" ?"active":""}`} onClick={()=>changeViewMode("list")}>List</button>
            </div>
            {adminMode
              ? <button className="admin-add-btn" onClick={()=>setShowEditPanel(true)}>Edit Schedule</button>
              : (!hideAdminBtn && !adminMode && <button className="admin-login-btn" onClick={()=>{ if (TOKEN) openAdmin(); else setShowPwModal(true); }}>Admin</button>)
            }
          </div>
        </div>

        {viewMode==="month" && (
          <MonthView {...sharedViewProps}
            calendarCells={calendarCells}
            renderWorkshopBanners={renderWorkshopBanners} />
        )}
        {viewMode==="week" && <WeekView {...sharedViewProps} weekDates={weekDates} />}
        {viewMode==="day"  && <DayView  {...sharedViewProps} focusDay={focusDay} />}
        {viewMode==="list" && !adminMode && (
          <AgendaView API={API} today={today} classes={classes} workshops={workshops} taken={taken}
            cancelledDates={cancelledDates} dateOverrides={dateOverrides} venues={venues}
            onBookClass={(evt, date) => { setSelectedEvent(evt); setSelectedDate(date); }}
            onBookWorkshop={setSelectedWorkshop} />
        )}
        {viewMode==="list" && adminMode && (
          <ListView
            adminMode={adminMode}
            venues={venues}
            classes={classes}
            cancelledDates={cancelledDates}
            dateOverrides={dateOverrides}
            privateBookings={privateBookings}
            today={today}
            setSelectedEvent={setSelectedEvent}
            setSelectedDate={setSelectedDate}
          />
        )}

        <div className={`cal-legend${viewMode==="list" ? " cal-legend--hidden" : ""}`}>
          {classTypes.length > 0
            ? classTypes.map(t => (
                <div key={t.name} className="legend-item" data-subj={typeKeywords(t.name)[0] || ""}><div className="legend-dot dot-type"/><span>{t.name}</span></div>
              ))
            : <>
                <div className="legend-item"><div className="legend-dot dot-studio"/><span>In Studio</span></div>
                <div className="legend-item"><div className="legend-dot dot-online"/><span>Online</span></div>
              </>}
          <div className="legend-item"><div className="legend-dot dot-workshop"/><span>Workshop</span></div>
          {classTypes.length === 0 && <div className="legend-item"><div className="legend-dot dot-hybrid"/><span>Hybrid</span></div>}
          {adminMode && <div className="legend-item"><div className="legend-dot dot-cancelled"/><span>Cancelled</span></div>}
        </div>
              <div className="yc-powered-by">
                <a href="https://florium.live" target="_blank" rel="noopener noreferrer" className="yc-powered-link">
                  Powered by <span className="yc-powered-name">florium.</span>
                </a>
          </div>
      </div>

      {showPwModal && (
        <PasswordModal
          onSuccess={() => { setShowPwModal(false); openAdmin(); }}
          onClose={() => setShowPwModal(false)} />
      )}

      {showEditPanel && (
        <AdminEditPanel
          classes={classes} workshops={workshops} venueList={venues}
          initialEditClass={editingClassFromModal}
          onSaveClass={handleSaveClass}       onDeleteClass={handleDeleteClass}
          onSaveWorkshop={handleSaveWorkshop} onDeleteWorkshop={handleDeleteWorkshop}
          onClose={()=>{ setShowEditPanel(false); setEditingClassFromModal(null); }} />
      )}

      {selectedWorkshop && !adminMode && (
        <Elements stripe={stripePromise}>
          <WorkshopBookingModal workshop={selectedWorkshop} onClose={closeModal} />
        </Elements>
      )}

      {selectedWorkshop && adminMode && (()=>{ setShowEditPanel(true); setSelectedWorkshop(null); return null; })()}

      {selectedEvent && selectedDate && (
        adminMode
          ? <AdminEventModal event={selectedEvent} date={selectedDate}
              cancelledDates={cancelledDates} dateOverrides={dateOverrides}
              venueList={venues}
              onCancelDate={handleCancelDate} onRestoreDate={handleRestoreDate}
              onSaveDateOverride={handleSaveDateOverride}
              onEditRecurring={handleEditRecurring}
              onClose={closeModal} />
          : <Elements stripe={stripePromise}>
              <EventModal event={selectedEvent} date={selectedDate} onClose={closeModal} prefill={deepPrefill} />
            </Elements>
      )}
       {passModalType && (
         <Elements stripe={stripePromise}>
           <PassPurchaseModal
             prefillPassType={passModalType}
             onClose={() => setPassModalType(null)}
             onPurchaseComplete={() => setPassModalType(null)}
           />
         </Elements>
       )}
    </>
  );
}